import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import PublicIcon from '@mui/icons-material/Public'
import { Stack, Tooltip } from '@mui/material'
import type { GridColDef, GridSortModel } from '@mui/x-data-grid'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import CustomDataGrid from '@/components/dataDisplay/CustomDataGrid'
import FlagIcon from '@/components/dataDisplay/FlagIcon'
import environment from '@/environment'
import { usePermissions } from '@/features/authorization/contexts/PermissionsContext'
import { PartnerFormDialog } from '@/features/partner/components/PartnerFormDialog'
import PartnersDataGridToolbar from '@/features/partner/components/PartnersDataGridToolbar'
import { useInvalidatePartnersQuery, usePartnersQuery } from '@/features/partner/hooks/usePartnersQuery'
import type { Partner } from '@/openapi/v1/msa-customer-partner-manager/partner'

const DEFAULT_DATA_GRID_PARTNERS_SORT_MODEL: GridSortModel = [{ field: 'name', sort: 'asc' }]

function Partners() {
  const { permissions } = usePermissions()
  const { t } = useTranslation()
  const locationInfos: LocationInfo[] = environment.getLocationsList()
  const [selectedPartner, setSelectedPartner] = useState({} as Partner)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editMode, setEditMode] = useState(false)

  const handleOpenDialog = (partner: Partner, editMode: boolean) => {
    setSelectedPartner(partner)
    setDialogOpen(true)
    setEditMode(editMode)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedPartner({} as Partner)
    setEditMode(false)
  }

  const { partners, isFetching } = usePartnersQuery()
  const invalidatePartnersQuery = useInvalidatePartnersQuery()

  function getCountryOptions(): CountryIdentifier[] {
    if (!partners) return []

    return Array.from(
      new Set(partners.filter((p) => !!p.countryCode).map((p) => p.countryCode!.toLowerCase() as CountryIdentifier)),
    )
  }

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('partners.partner_name'),
      flex: 1,
    },
    {
      field: 'partnerCode',
      headerName: t('partners.symbolic_name'),
      renderHeader: () => (
        <Stack alignItems="center" className={'MuiDataGrid-columnHeaderTitle'} direction="row" gap={1}>
          <strong style={{ fontWeight: 500 }}>{t('partners.symbolic_name')}</strong>
          <Tooltip title={t('partners.symbolic_name_hint')}>
            <HelpOutlineIcon fontSize="small" sx={{ color: '#000' }} />
          </Tooltip>
        </Stack>
      ),
      flex: 1,
    },
    {
      field: 'countryCode',
      headerName: t('common.country.label'),
      flex: 1,
      type: 'singleSelect',
      valueOptions: getCountryOptions(),
      getOptionLabel: (value) => (value ? t(`common.country.${value as CountryIdentifier}`) : '-'),
      valueGetter: (value: Partner['countryCode']) => value?.toLowerCase(),
      renderHeader: () => (
        <Tooltip title={t('common.country.label')}>
          <PublicIcon />
        </Tooltip>
      ),
      renderCell: (params) => (
        <Stack sx={{ flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
          <FlagIcon countryCode={params.row.countryCode?.toLowerCase()} />
        </Stack>
      ),
    },
  ]

  return (
    <>
      <CustomDataGrid
        clickableRows={
          permissions.has('partnersMutation')
            ? {
                onRowClick: (params) => {
                  handleOpenDialog(params.row, true)
                },
              }
            : undefined
        }
        columns={columns}
        getRowId={(row) => row.uuid}
        initialState={{
          sorting: {
            sortModel: DEFAULT_DATA_GRID_PARTNERS_SORT_MODEL,
          },
        }}
        isLoading={isFetching}
        rows={partners?.filter((partner) => partner.partnerCode !== 'SYMPOWER') ?? []}
        slotProps={{
          toolbar: {
            countryOptions: getCountryOptions(),
            onClickAddNewPartner: () => handleOpenDialog({} as Partner, false),
          },
        }}
        slots={{
          toolbar: PartnersDataGridToolbar,
        }}
      />

      {dialogOpen && (
        <PartnerFormDialog
          editMode={editMode}
          locationInfos={locationInfos}
          open={dialogOpen}
          partner={selectedPartner}
          onClose={handleCloseDialog}
          onSave={() => invalidatePartnersQuery()}
        />
      )}
    </>
  )
}

export default Partners
